import { type FunctionComponent } from 'react';
import { type ImageProps } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { type OpenApiSchemaMediaItem, type OpenApiSchemaMediaItemV2 } from '../../api/schema';
import { AdaptiveImage, AdaptiveMedia } from '../AdaptiveImage';
import { ImageBlank } from '../ImageBlank';
import { MediaZoomPopup, type MediaZoomPopupProps } from '../MediaZoomPopup';

export interface MediaPreviewProps extends TestProps, Omit<ImageProps, 'src'> {
    media: OpenApiSchemaMediaItemV2[] | OpenApiSchemaMediaItem[] | undefined;
    withZoomPopup?: boolean;
    zoomWidth?: MediaZoomPopupProps['width'];
    zoomHeight?: MediaZoomPopupProps['height'];
    zoomPlacement?: MediaZoomPopupProps['placement'];
}

export const MediaPreview: FunctionComponent<MediaPreviewProps> = ({
    'data-testid': dataTestId = 'MediaPreview',
    media,
    width,
    height,
    borderRadius,
    background,
    withZoomPopup = false,
    zoomHeight = 200,
    zoomWidth = 200,
    zoomPlacement,
    ...props
}) => {
    const firstMedia = media?.[0];

    const isMediaItemV2 = firstMedia && 'payload' in firstMedia;

    let src: string | undefined;

    if (isMediaItemV2) {
        src =
            'file_path' in firstMedia?.payload
                ? firstMedia?.payload?.file_path
                : firstMedia?.payload?.content?.first_frame_url;
    } else {
        src = firstMedia?.file_path;
    }

    if (!src || !firstMedia) {
        return (
            <ImageBlank
                data-testid={dataTestId}
                width={width}
                height={height}
                background={background}
                borderRadius={borderRadius}
                className={props.className}
            />
        );
    }

    const mediaNode = isMediaItemV2 ? (
        <AdaptiveMedia
            data-testid={dataTestId}
            {...props}
            media={firstMedia}
            width={width}
            height={height}
            background={background}
            borderRadius={borderRadius}
        />
    ) : (
        <AdaptiveImage
            data-testid={dataTestId}
            {...props}
            media={firstMedia}
            width={width}
            height={height}
            background={background}
            borderRadius={borderRadius}
        />
    );

    if (!withZoomPopup) {
        return mediaNode;
    }

    return (
        <MediaZoomPopup
            data-testid={`${dataTestId}.Zoom`}
            height={zoomHeight}
            width={zoomWidth}
            alt={props.alt}
            media={firstMedia}
            borderRadius="8"
            placement={zoomPlacement}
            mediaNode={mediaNode}
        />
    );
};
